import {
  AttachmentModel,
  MediaThumbnail,
} from '@fc-vehicles/service/attachment-model';

/**
 * Longest-edge pixel bound the backend generates for each thumbnail tier
 * (single source of truth: backend `utils/thumbnails.py` `ThumbnailSizes`).
 */
export const THUMBNAIL_BOUNDS = {
  sm: 150,
  md: 300,
  lg: 600,
  xl: 1200,
  xxl: 2400,
} as const;

export type ThumbnailTier = keyof typeof THUMBNAIL_BOUNDS;

export const THUMBNAIL_TIERS = Object.keys(THUMBNAIL_BOUNDS) as ThumbnailTier[];

/**
 * Picks the server-generated thumbnail URL whose bound covers the rendered
 * size (doubled on HiDPI screens). Returns `undefined` when the attachment
 * has no thumbnails, so callers can fall back to the full image.
 */
export function pickThumbnailUrl(
  photo: AttachmentModel | null | undefined,
  renderPx: number,
): string | undefined {
  if (!photo) return undefined;
  const thumbnails = photo.thumbnails;
  if (!hasThumbnailTiers(thumbnails)) {
    warnMissingThumbnails(photo);
    return undefined;
  }
  const isHiDPI = window.devicePixelRatio > 1;
  const targetPx = (renderPx || THUMBNAIL_BOUNDS.md) * (isHiDPI ? 2 : 1);

  for (const tier of THUMBNAIL_TIERS) {
    if (thumbnails[tier]?.url && THUMBNAIL_BOUNDS[tier] >= targetPx) {
      return thumbnails[tier].url;
    }
  }
  return thumbnails.xxl?.url;
}

/**
 * Backend-generated thumbnail tiers as `<url> <width>w` srcset entries, so the
 * browser picks the tier for the rendered size and DPR. Camera-snapshot tiers
 * come without dimensions; the tier's longest-edge bound is a close-enough
 * width descriptor.
 */
export function buildThumbnailSrcset(
  tiers: MediaThumbnail | undefined,
): string[] {
  if (!tiers) return [];
  const entries: string[] = [];
  for (const tier of THUMBNAIL_TIERS) {
    const entry = tiers[tier];
    if (!entry?.url) continue;
    entries.push(`${entry.url} ${entry.width ?? THUMBNAIL_BOUNDS[tier]}w`);
  }
  return entries;
}

/**
 * Resolves a media field that the API serves either as a plain URL string or
 * as a thumbnail envelope, to the URL matching the rendered size.
 */
export function resolveMediaUrl(
  media: string | AttachmentModel | null | undefined,
  renderPx: number,
): string | undefined {
  if (!media) return undefined;
  if (typeof media === 'string') return media;
  return (
    pickThumbnailUrl(media, renderPx) ||
    media.scaledImg ||
    media.previewImg ||
    media.original
  );
}

export function hasThumbnailTiers(
  thumbnails: MediaThumbnail | undefined,
): thumbnails is MediaThumbnail {
  return !!thumbnails && THUMBNAIL_TIERS.some((tier) => thumbnails[tier]?.url);
}

const warnedAttachments = new Set<string | number>();

/**
 * The backend is expected to serve `thumbnails` on every image attachment;
 * rendering the full-size original is a backend gap — warn once per
 * attachment so it can be reported.
 */
export function warnMissingThumbnails(photo: AttachmentModel): void {
  const originalUrl = photo.previewImg || photo.original || photo.scaledImg;
  if (!originalUrl || typeof originalUrl !== 'string') return;

  const attachmentKey = photo.id ?? originalUrl;
  if (warnedAttachments.has(attachmentKey)) return;
  warnedAttachments.add(attachmentKey);

  console.warn(
    `[image-audit] Attachment${photo.id ? ` #${photo.id}` : ''} came without server thumbnails — rendering the original image: ${originalUrl}`,
  );
}
